import React, { useState } from 'react';
import api from '../api';
import { getErrorMessage } from '../types';
import { useTranslation } from '../i18n';

interface Props {
  onAccepted?: () => void;
}

export const SelfBillingAcceptance: React.FC<Props> = ({ onAccepted }) => {
  const { t } = useTranslation();
  const [checked, setChecked] = useState(false);
  const [sellerType, setSellerType] = useState<'private' | 'business' | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [accepted, setAccepted] = useState(false);

  const handleAccept = async () => {
    if (!checked || sellerType !== 'private') return;
    setBusy(true);
    setError('');
    try {
      await api.post('/connect/accept-self-billing', { isBusinessSeller: false });
      setAccepted(true);
      onAccepted?.();
    } catch (err) {
      setError(getErrorMessage(err, t('self_billing.accept_error')));
    } finally {
      setBusy(false);
    }
  };

  if (accepted) {
    return <div className="alert alert-success">{t('self_billing.accepted')}</div>;
  }

  return (
    <div className="card" style={{ marginTop: '1rem' }}>
      <h3 style={{ marginTop: 0 }}>{t('self_billing.title')}</h3>
      <p className="text-muted" style={{ fontSize: '0.9rem' }}>{t('self_billing.description')}</p>
      {error && <div className="alert alert-error">{error}</div>}
      <fieldset style={{ border: 'none', padding: 0, margin: '0 0 0.75rem' }}>
        <legend style={{ fontSize: '0.9rem', fontWeight: 600, marginBottom: '0.25rem' }}>{t('self_billing.seller_type_label')}</legend>
        {(['private', 'business'] as const).map(type => (
          <label key={type} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.9rem', cursor: 'pointer' }}>
            <input
              type="radio"
              name="seller-type"
              checked={sellerType === type}
              onChange={() => setSellerType(type)}
              disabled={busy}
              style={{ marginTop: '0.2rem' }}
            />
            <span>{t(`self_billing.seller_type_${type}`)}</span>
          </label>
        ))}
      </fieldset>
      {sellerType === 'business' && (
        <div className="alert alert-error">{t('self_billing.business_not_supported')}</div>
      )}
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem', fontSize: '0.9rem', cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={checked}
          onChange={e => setChecked(e.target.checked)}
          disabled={busy}
          style={{ marginTop: '0.2rem' }}
        />
        <span>{t('self_billing.accept_label')}</span>
      </label>
      <div className="form-actions" style={{ marginTop: '0.75rem' }}>
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleAccept}
          disabled={!checked || sellerType !== 'private' || busy}
        >
          {busy && <span className="btn-spinner" aria-hidden="true" />}
          {t('self_billing.continue_button')}
        </button>
      </div>
    </div>
  );
};
